import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(!!localStorage.getItem('auth_token'));

  const isAuthenticated = !!token && !!user;

  // Fetch user on mount if token exists
  useEffect(() => {
    if (token) {
      authService.getUser()
        .then((response) => {
          setUser(response.data?.data ?? response.data?.user ?? response.data);
        })
        .catch(() => {
          // Token invalid, clean up
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    }
  }, [token]);

  const login = useCallback(async (credentials) => {
    const response = await authService.login(credentials);
    const data = response.data;
    const newToken = data.token || data.access_token;
    const userData = data.user || data.data;

    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);

    return data;
  }, []);

  const register = useCallback(async (formData) => {
    const response = await authService.register(formData);
    const data = response.data;
    const newToken = data.token || data.access_token;
    const userData = data.user || data.data;

    if (!newToken) {
      throw new Error('No se pudo completar el registro. Intente de nuevo.');
    }

    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);

    return data;
  }, []);

  const loginWithGoogle = useCallback(async (googleCredential) => {
    /* AgendaMax verifica la firma del id_token y con flow 'client' hace
       find-or-create del cliente por email: el dedupe vive en el backend.
       (Sin fallbacks locales: registrar aquí con contraseña aleatoria
       creaba clientes duplicados sin pasar por ese dedupe.) */
    const response = await authService.googleAuth({
      id_token: googleCredential,
      flow: 'client',
    });
    const data = response.data;
    const newToken = data.token || data.access_token;
    const userData = data.user || data.data;

    if (!newToken) throw new Error('No se pudo iniciar sesión con Google. Intente de nuevo.');

    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);

    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore logout API errors
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      setToken(null);
      setUser(null);
    }
  }, []);

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
